import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Container, Paper, Typography } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { useResolverEntradaPorEtiquetaQuery } from '../../api';

/**
 * `/entrada/:etiqueta` — porta de entrada do cupom da BALANÇA de
 * autoatendimento.
 *
 * Ao imprimir, a retaguarda só troca `{{identificadorPrincipal}}` pela etiqueta
 * ASSINADA da comanda dentro do parâmetro
 * `LINK_IMPRESSAO_QRCODE_AUTOATENDIMENTO_BALANCA` — não há chamada ao cardápio
 * nessa hora. Então a tradução etiqueta → código do ponto de acesso acontece
 * aqui, no celular do cliente, e o resto do fluxo é o mesmo `/checkout/<codigo>`
 * do QR de mesa.
 *
 * A etiqueta (`SCF1.C.5.aB3dEf9k2Q`) é opaca para esta tela de propósito: quem
 * confere a assinatura é o servidor. Decodificar aqui para enfeitar a mensagem
 * de erro duplicaria a verificação no cliente — daí o texto de falha ser
 * genérico. Antes o link trazia o número cru da comanda, e como comanda é 1, 2,
 * 3… dava para enumerar a conta do vizinho pelo link público da loja.
 *
 * Esta tela não monta o catálogo de propósito: só resolve e sai.
 */
export function EntradaNumeroPage(): JSX.Element {
  const { etiqueta } = useParams();

  const etiquetaInformada = (etiqueta ?? '').trim();
  const podeResolver = etiquetaInformada.length > 0;

  const { data, isError } = useResolverEntradaPorEtiquetaQuery(
    { etiqueta: etiquetaInformada },
    { skip: !podeResolver },
  );

  useEffect(() => {
    if (!data) return;
    // Navegação de PÁGINA, e não `<Navigate>`: o tenant é resolvido uma única
    // vez no boot (`main.tsx` → `inicializarTenant`), a partir da URL. Um
    // redirect client-side deixaria o app com o tenant do HOST — e o pedido
    // sairia como delivery, fora da comanda. Recarregando em
    // `/checkout/<codigo>` o app repete exatamente o caminho do QR de mesa.
    window.location.replace(`/checkout/${data.codigo}`);
  }, [data]);

  if (podeResolver && !isError) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">Abrindo seu pedido…</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ pt: 8 }}>
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ mb: 2 }}>
          <ReceiptLongOutlinedIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
        </Box>
        <Typography variant="h5" gutterBottom>
          Não encontramos esta comanda ou mesa
        </Typography>
        <Typography color="text.secondary">
          Chame um atendente e mostre este cupom.
        </Typography>
      </Paper>
    </Container>
  );
}
